import { jwtDecode } from 'jwt-decode';

/**
 * Extract user information from a JWT token
 * @param {string} token - The JWT token
 * @returns {Object} User information extracted from the token
 */
export const extractUserFromJWT = (token) => {
  if (!token) return {};

  try {
    // Use the imported jwtDecode function directly
    const decoded = jwtDecode(token);

    // Extract only the user ID from the JWT payload
    // The Django backend only includes user_id in the JWT token
    return {
      userId: decoded.sub || decoded.user_id || decoded.id,
    };
  } catch (error) {
    console.error('Error extracting user from JWT:', error);
    return {};
  }
};

/**
 * Check if a JWT token will expire within the specified time
 * @param {string} token - The JWT token to check
 * @param {number} withinMinutes - Minutes threshold to check expiration against
 * @returns {boolean} True if token will expire within the specified time
 */
export const willTokenExpire = (token, withinMinutes = 10) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return false;

    // Get current time in seconds (JWT exp is in seconds)
    const currentTime = Math.floor(Date.now() / 1000);

    // Calculate expiration threshold in seconds
    const thresholdTime = currentTime + (withinMinutes * 60);

    // Return true if token will expire within the threshold time
    return decoded.exp < thresholdTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume token will expire if we can't check it
  }
};

/**
 * Check if a JWT token has expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} True if token has expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return false;

    // Get current time in seconds (JWT exp is in seconds)
    const currentTime = Math.floor(Date.now() / 1000);

    // Return true if token has expired
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume token has expired if we can't check it
  }
};

/**
 * @param {string} token - The JWT token to decode and log
 */
export const logJWTContents = (token) => {
  if (!token) {
    return;
  }
};
